import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Dropdown from '@components/drop-down/Dropdown';
import UserPermissionTypes from '@constants/permissions';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import useDownloadReport from '@features/reports/hooks/downloadReport';
import { isSalesAgent as isSalesAgentScope } from '@helpers/roleScope';
import useCheckPermission from '@hooks/checkPermission';
import useNotify from '@hooks/notify';
import Head from '@layouts/head/Head';
import { getLocalStorageItem } from '@utils/storage';

import {
  AgentReportAnalysis,
  LeadReportAnalysis
} from '../components/lead-report-analysis';
import { INPUT_FOCUS_CLASSNAME } from '../constants/common';
import {
  INITIAL_FORM_DATA,
  JobTypeEnum,
  ReportTypeEnum,
  ReportTypeMap,
  reportTypeOptions
} from '../constants/lead-report';
import { LeadReportFormData } from '../types/leadReport';
import { GenerateReportRequest } from '../types/request';

const LeadReports = () => {
  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );
  const { currentOrg, rolesAndPermissions, currentUser } = useSelector(
    (state: any) => state.user
  );
  const isSalesAgent = isSalesAgentScope(rolesAndPermissions);
  const { checkPageAccess } = useCheckPermission();

  const { handleDownloadLeadReport } = useDownloadReport();
  const { createErrorAlert } = useNotify();

  const [selectedReportType, setSelectedReportType] = useState<ReportTypeEnum>(
    ReportTypeEnum.LEADS_OVERVIEW
  );
  const [formData, setFormData] =
    useState<LeadReportFormData>(INITIAL_FORM_DATA);

  useEffect(() => {
    checkPageAccess({
      permission: [UserPermissionTypes.LEAD_REPORT_GENERATE]
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSalesAgent && currentUser?.user_id) {
      setFormData((prev) => ({
        ...prev,
        assignedAgent: [
          {
            id: currentUser.user_id,
            value: currentUser.user_id,
            label: currentUser.name
          }
        ]
      }));
    }
  }, [isSalesAgent, currentUser]);

  const [allOptionsSelectedFields, setAllOptionsSelectedFields] = useState<{
    location: boolean;
    leadCategory: boolean;
    leadStatus: boolean;
    source: boolean;
    tags: boolean;
    assignedAgent: boolean;
  }>({
    location: false,
    leadCategory: false,
    leadStatus: false,
    source: false,
    tags: false,
    assignedAgent: false
  });

  const handleReportTypeChange = (value: string) => {
    setSelectedReportType(value as ReportTypeEnum);
    setFormData(
      isSalesAgent && currentUser?.user_id
        ? {
            ...INITIAL_FORM_DATA,
            assignedAgent: [
              {
                id: currentUser.user_id,
                value: currentUser.user_id,
                label: currentUser.name
              }
            ]
          }
        : INITIAL_FORM_DATA
    );
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAllOptionsSelection = (field: string, isAllSelected: boolean) => {
    setAllOptionsSelectedFields((prev) => ({
      ...prev,
      [field]: isAllSelected
    }));
  };

  const handleDownloadReport = async () => {
    try {
      const reportType: JobTypeEnum =
        selectedReportType === ReportTypeEnum.LEADS_OVERVIEW
          ? ReportTypeMap[ReportTypeEnum.LEADS_OVERVIEW]
          : ReportTypeMap[ReportTypeEnum.AGENT_OVERVIEW];

      const dateRange = {
        start_date: new Date(formData.startDate),
        end_date: new Date(formData.endDate)
      };

      const locations = allOptionsSelectedFields.location
        ? ['all']
        : formData.location.map((location) => String(location.id));

      const leadCategories = allOptionsSelectedFields.leadCategory
        ? ['all']
        : formData.leadCategory.map((category) => category.value);

      const leadStatus = allOptionsSelectedFields.leadStatus
        ? ['all']
        : formData.leadStatus.map((status) => String(status.id));

      const sources = allOptionsSelectedFields.source
        ? ['all']
        : formData.source.map((source) => String(source.id));

      const tags = allOptionsSelectedFields.tags
        ? ['all']
        : formData.tags.map((tag) => tag.value);

      const assignedAgents = allOptionsSelectedFields.assignedAgent
        ? ['all']
        : formData.assignedAgent.map((agent) => agent.value);

      const requestPayload: GenerateReportRequest = {
        report_type: reportType,
        date_range: dateRange,
        ...(!!assignedAgents.length && {
          assigned_agents: assignedAgents
        }),
        ...(!!locations.length && {
          cities: locations
        }),
        ...(!!leadStatus.length && {
          status: leadStatus
        }),
        ...(!!sources.length && {
          sources
        }),
        ...(!!tags.length && {
          tags
        }),
        ...(!!leadCategories.length && {
          categories: leadCategories
        })
      };

      await handleDownloadLeadReport(organizationId, requestPayload);

      // Mix panel event for report generated
      const { date_range: dateRangePayload, ...filtersApplied } =
        requestPayload;
      sendMixpanelEvent(Event.LEAD_REPORT_GENERATED, {
        org_id: organizationId,
        org_name: currentOrg?.name,
        report_type: reportType,
        date_range: dateRangePayload
          ? {
              start_date: dateRangePayload.start_date.toISOString(),
              end_date: dateRangePayload.end_date.toISOString()
            }
          : undefined,
        filters_applied: filtersApplied
      });
    } catch (error) {
      createErrorAlert('Failed to generate report. Please try again.');
    }
  };

  const isFormValid = (): boolean => {
    if (selectedReportType === ReportTypeEnum.LEADS_OVERVIEW) {
      return Boolean(
        formData.startDate &&
          formData.startDate !== '' &&
          formData.endDate &&
          formData.endDate !== ''
      );
    }
    if (selectedReportType === ReportTypeEnum.AGENT_OVERVIEW) {
      return Boolean(
        formData.startDate &&
          formData.startDate !== '' &&
          formData.endDate &&
          formData.endDate !== '' &&
          formData.assignedAgent.length > 0
      );
    }
    return false;
  };

  return (
    <div className="page my-6 space-y-6 bg-bgBase">
      <Head title="Reports" />
      <h1 className="text-2xl font-bold text-content">Reports & Analytics</h1>
      <p className="!mt-0 text-base font-normal text-grey">
        Generate detailed reports and track performance metrics
      </p>

      <div className="rounded-xl bg-white p-6">
        <div className="w-full lg:w-[calc(50%-1rem)] xl:w-[calc(33.333%-1rem)]">
          <label className="mb-1 block text-sm font-semibold text-cofeeNeutral10">
            Report Type
          </label>
          <Dropdown
            currentFilter={selectedReportType}
            filterOptions={reportTypeOptions}
            onSelectFilter={handleReportTypeChange}
            placeholder="Select Report Type"
            className={`h-12.5 w-full rounded-lg ${INPUT_FOCUS_CLASSNAME} !border-borderGrey7`}
            optionsStyle="w-full max-h-56 overflow-y-auto"
          />
        </div>

        <div className="border-grey/30 my-6 border-t border-dashed" />

        <div>
          {selectedReportType === ReportTypeEnum.LEADS_OVERVIEW ? (
            <LeadReportAnalysis
              formData={formData}
              onFormChange={handleFormChange}
              onDownloadReport={handleDownloadReport}
              isFormValid={isFormValid()}
              handleAllOptionsSelection={handleAllOptionsSelection}
            />
          ) : (
            <AgentReportAnalysis
              formData={{
                leadAssignmentDate: formData.leadAssignmentDate || '',
                assignedAgent: formData.assignedAgent
              }}
              onFormChange={handleFormChange}
              onDownloadReport={handleDownloadReport}
              isFormValid={isFormValid()}
              handleAllOptionsSelection={handleAllOptionsSelection}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadReports;
